// src/hooks/useTransactions.ts
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { TransactionsResponse } from '../types';
import { axiosInstance } from 'src/api';
import { IEVMTransaction } from 'src/types';

export interface UseTransactionsProps {
  chain: string;
  address: string;
  activityType?: 'native' | 'erc20' | null;
  page?: number;
  limit?: number;
}

export interface TransactionsResult {
  transactions: IEVMTransaction[];
  pagination: {
    totalPages: number;
    totalDocs: number;
    currentPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

const transformResponse = (res: TransactionsResponse): TransactionsResult => ({
  transactions: res.data.docs,
  pagination: {
    totalPages: res.data.totalPages,
    totalDocs: res.data.totalDocs,
    currentPage: res.data.page,
    hasNextPage: res.data.hasNextPage,
    hasPrevPage: res.data.hasPrevPage,
  },
});

export function useTransactions({
  chain,
  address,
  activityType,
  page = 1,
  limit = 25,
}: UseTransactionsProps): UseQueryResult<TransactionsResult, Error> {
  return useQuery({
    queryKey: ['transactions', chain, address, activityType, page, limit],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(activityType ? { activityType } : {}),
      });
      const url = `/${chain}/address/${address}/transactions?${params}`;
      const res = await axiosInstance.get<TransactionsResponse>(url);

      if (!res.data.success || !res.data.data?.docs) {
        throw new Error('Malformed API response');
      }

      return transformResponse(res.data);
    },
    staleTime: 5 * 60 * 1000, // 5m
    enabled: !!chain && !!address,
  });
}
